export interface ParsedError {
  title: string;
  message: string;
  type: 'timeout' | 'network' | 'area_limit' | 'validation' | 'processing' | 'generic';
  icon: 'Clock' | 'Wifi' | 'MapPin' | 'AlertTriangle' | 'AlertCircle';
  color: 'amber' | 'red' | 'blue';
  retriable: boolean;
}

export function parsePreviewError(rawError: string | null): ParsedError {
  if (!rawError) {
    return {
      title: 'Something went wrong',
      message: 'An unexpected error occurred. Please try again.',
      type: 'generic',
      icon: 'AlertCircle',
      color: 'red',
      retriable: true,
    };
  }

  const lower = rawError.toLowerCase();

  // Timeout
  if (lower.includes('timeout') || lower.includes('timed out') || lower.includes('taking longer')) {
    return {
      title: 'Taking longer than expected',
      message: 'The preview is taking too long to generate. Try a smaller area or try again later.',
      type: 'timeout',
      icon: 'Clock',
      color: 'amber',
      retriable: true,
    };
  }

  // Network / server unreachable
  // Match specific patterns to avoid false positives from broad keywords:
  // - "Failed to fetch" = browser generic for CORS, DNS, or network failure
  // - "NetworkError" = explicit network error
  // - "ECONNREFUSED" = connection refused
  // - "ERR_FAILED" = Chrome network error
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('econnrefused') ||
    lower.includes('err_failed') ||
    lower.includes('network error') ||
    lower.includes('load failed')
  ) {
    return {
      title: 'Preview service unavailable',
      message: "Couldn't reach the preview servers. This may be temporary — please try again in a moment.",
      type: 'network',
      icon: 'Wifi',
      color: 'amber',
      retriable: true,
    };
  }

  // Area limit — NOT retriable
  if (lower.includes('area') && (lower.includes('large') || lower.includes('km'))) {
    return {
      title: 'Area too large',
      message: 'The selected area is too large for preview. Please zoom in to a smaller area.',
      type: 'area_limit',
      icon: 'MapPin',
      color: 'blue',
      retriable: false,
    };
  }

  // Validation / Pydantic — NOT retriable
  if (rawError.includes('"detail"') || rawError.includes('"loc"') || rawError.includes('"type":"value_error') || lower.includes('invalid request')) {
    return {
      title: 'Invalid selection',
      message: 'Please check your location and settings, then try again.',
      type: 'validation',
      icon: 'AlertTriangle',
      color: 'amber',
      retriable: false,
    };
  }

  // Geocoding failure — NOT retriable
  if (lower.includes('geocod') || lower.includes('address not found') || lower.includes('location not found')) {
    return {
      title: 'Location not found',
      message: "Couldn't find this address. Please try a different location.",
      type: 'validation',
      icon: 'MapPin',
      color: 'amber',
      retriable: false,
    };
  }

  // No building data — NOT retriable
  if (lower.includes('no building') || lower.includes('no osm') || lower.includes('empty result')) {
    return {
      title: 'No building data',
      message: 'No building data found for this location. Try selecting a more urban area.',
      type: 'processing',
      icon: 'MapPin',
      color: 'blue',
      retriable: false,
    };
  }

  // Processing errors (OOM, Java, etc) — retriable with smaller area
  if (lower.includes('outofmemory') || lower.includes('oom') || lower.includes('java.') || lower.includes('processing')) {
    return {
      title: 'Processing error',
      message: 'This area is too complex to process. Try a smaller or less dense area.',
      type: 'processing',
      icon: 'AlertTriangle',
      color: 'amber',
      retriable: true,
    };
  }

  // Short non-technical messages — pass through
  if (rawError.length < 150 && !_containsTechnicalDetail(rawError)) {
    return {
      title: 'Something went wrong',
      message: rawError,
      type: 'generic',
      icon: 'AlertCircle',
      color: 'red',
      retriable: true,
    };
  }

  // Fallback for unknown/long/technical errors
  return {
    title: 'Preview generation failed',
    message: 'An error occurred while generating the preview. Please try again or contact support.',
    type: 'generic',
    icon: 'AlertCircle',
    color: 'red',
    retriable: true,
  };
}

function _containsTechnicalDetail(msg: string): boolean {
  return /Traceback|File\s+"|java\.|at\s+\w+\.\w+\(|gs:\/\/|\.py:\d+/.test(msg);
}
